import {
  ComponentRef,
  Directive,
  inject,
  Input,
  OnInit,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthService } from '@auth0/auth0-angular';
import { InjectableSuperclass } from '@sama/ng-core';
import { pick } from 'lodash';
import { filter, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { customMaterialIcons as custom3DMaterialIcons } from '../3d/utils/custom-icons';
import {
  getCacheKey,
  resourcePrefixCacheKey,
} from '../core/http/abstract-resource';
import { DebugLoggingService } from '../core/shared/debug-logging/debug-logging.service';
import { customMaterialIcons as customCoreMaterialIcons } from '../core/shared/icons/custom-icons';
import { registerCustomMaterialIcons } from '../cross-team-shared/icons/register-custom-icons';
import { BrowserControlService } from '../cross-team-shared/misc-services/browser-control.service';
import { User } from '../team-apis/otterdam/user';
import { AbstractWorkspaceComponent } from './abstract-workspace-component';
import { WorkspaceLoadingService } from './misc-services/workspace-loading.service';

const abstractWorkspaceInputKeys = ['currentUser'] as const;

export type AbstractWorkspaceInputs = Pick<
  AbstractWorkspaceComponent,
  (typeof abstractWorkspaceInputKeys)[number]
>;

@Directive()
export abstract class AbstractWorkspaceHostComponent<
    T extends AbstractWorkspaceComponent,
  >
  extends InjectableSuperclass
  implements OnInit, AbstractWorkspaceInputs
{
  @Input({ required: true }) currentUser!: User;
  @ViewChild('container', { read: ViewContainerRef, static: true })
  viewContainerRef!: ViewContainerRef;

  private readonly browserControlService = inject(BrowserControlService);
  private readonly workspaceLoadingService = inject(WorkspaceLoadingService);
  private readonly debugLoggingService = inject(DebugLoggingService);
  private readonly authService = inject(AuthService);

  // used for test context to access workspace providers
  currentWorkspace!: ComponentRef<T>;
  isLoading$ = this.workspaceLoadingService.isLoading$.asObservable();

  protected constructor(
    private componentType: Type<T>,
    private inputKeys: Readonly<Array<keyof T>>,
  ) {
    super();
    this.componentType = componentType;

    registerCustomMaterialIcons(inject(MatIconRegistry), inject(DomSanitizer), [
      ...custom3DMaterialIcons,
      ...customCoreMaterialIcons,
    ]);
  }

  async ngOnInit() {
    await this.redirectIfNotAuthenticated();

    void this.browserControlService.deleteCache(
      getCacheKey(this.getProjectId(), resourcePrefixCacheKey),
    );

    if (window.STANDALONE) {
      this.loadAppcues();
    }

    this.debugLoggingService.initialize(this.currentUser);

    // create and show initial component. subsequent components will be hidden
    // until models are loaded while displaying the previous component
    showComponent(this.createComponent());
    this.workspaceLoadingService.onReset(() => {
      this.createComponent();
    });
  }

  private async redirectIfNotAuthenticated() {
    const isAuthenticated = await firstValueFrom(
      this.authService.isAuthenticated$,
    );

    if (!isAuthenticated) {
      await firstValueFrom(
        this.authService.loginWithRedirect({
          appState: {
            target: `${window.location.pathname}${window.location.search}`,
            href: window.location.href,
          },
        }),
      );
    }
  }

  private createComponent() {
    this.workspaceLoadingService.isLoading$.next(true);
    this.currentWorkspace = this.createNewHiddenWorkspaceComponent();

    this.currentWorkspace.instance.subscribeTo(
      this.isWorkspaceComponentLoaded$(this.currentWorkspace),
      () => {
        showComponent(this.currentWorkspace);
        this.removePreviousWorkspaceComponent(this.currentWorkspace);
      },
    );

    this.assignInputs(this.currentWorkspace);
    return this.currentWorkspace;
  }

  private assignInputs(newWorkspaceComponent: ComponentRef<T>) {
    Object.assign(
      newWorkspaceComponent.instance,
      pick(this, ...abstractWorkspaceInputKeys, ...this.inputKeys),
    );
  }

  private createNewHiddenWorkspaceComponent() {
    const component = this.viewContainerRef.createComponent(this.componentType);
    hideComponent(component);
    return component;
  }

  private isWorkspaceComponentLoaded$(component: ComponentRef<T>) {
    return component.instance.modelsLoading$.pipe(
      filter((modelsLoading) => modelsLoading === 'loaded'),
      take(1),
    );
  }

  private removePreviousWorkspaceComponent(
    newWorkspaceComponent: ComponentRef<T>,
  ) {
    const newIndex = this.viewContainerRef.indexOf(
      newWorkspaceComponent.hostView,
    );

    if (newIndex > 0) {
      this.viewContainerRef.remove(newIndex - 1);
    }
  }

  private loadAppcues() {
    window.Appcues.identify(`${this.currentUser.id}`, {
      teamId: `${this.currentUser.teamId ?? ''}`,
      clientId: `${this.currentUser.clientId ?? ''}`,
    });
    window.Appcues.track('page');
    window.Appcues.loadLaunchpad('#appcues-launchpad', {
      position: 'left',
      header: '<h4>Announcements</h4>',
      icon: '/assets/appcues-icon.svg',
    });
  }

  protected abstract getProjectId(): number;
}

function hideComponent(component: ComponentRef<any>) {
  component.location.nativeElement.style = 'display: none';
}

function showComponent(component: ComponentRef<any>) {
  component.location.nativeElement.style = '';
}
